import React, { useEffect, useMemo, useState } from 'react';

import { Popover } from '@mui/material';
import { noop } from 'lodash';
import { useParams } from 'react-router-dom';

import useHashmapConverter from 'hooks/useHashmapConverter';

import { useQueryGetProjectDatesForResourceChart } from 'api/queries/getProjectDatesForResourceChart.query';

import { SideMenu } from 'components/common/CustomFilters/DateRangeFilter/SideMenu/SideMenu';
import { getInputLabel } from 'components/common/CustomFilters/TableDateRangeFilter/TableDateRangeFilter.utils';
import { FiltersStatus } from 'components/common/GlobalFiltersInPage/GlobalFiltersInPage';
import classes from 'components/common/GlobalFiltersInPage/GlobalFiltersInPage.module.scss';
import { Range } from 'components/common/GlobalFiltersInPage/components/RangeFilter/rangeFilter';
import componentClasses from 'components/common/GlobalFiltersInPage/components/dateRangeFilter/dateRangeFilter.module.scss';
import { DateRangeFilterMain } from 'components/common/GlobalFiltersInPage/components/dateRangeFilter/dateRangeFilterMain';
import { signalSingleFilterIsReady } from 'components/common/GlobalFiltersInPage/hooks/useSubscribersFiltersReady';

import { ProgramStatusUrlParams } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/ProgramStatusSummaryReport/programStatusSummaryReportTypes';
import { TextInput } from 'components/DesignSystem/TextInput/TextInput';

export type RangeWithCustom = Range | 'R_CUSTOM';

export const rangeOptions: ISelectOption<RangeWithCustom>[] = [
    { label: '2 Weeks', value: 'R_2W' },
    { label: '4 Weeks', value: 'R_4W' },
    { label: '3 Months', value: 'R_3M' },
    { label: '6 Months', value: 'R_6M' },
    { label: '12 Months', value: 'R_12M' },
    { label: 'Custom', value: 'R_CUSTOM' },
];

interface Props {
    methods?: any;
    name: string;
    filtersStatusRef?: React.MutableRefObject<FiltersStatus>;
    handleFilters?: () => void;
    versionId?: number | undefined;
    compareVersionId?: number | undefined;
    value?: string;
}

export const DateRangeFilter = ({
    methods,
    name,
    filtersStatusRef,
    handleFilters,
    versionId,
    compareVersionId,
    value,
}: Props) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [menuOpen, setMenuOpen] = useState<boolean>(false);
    const [activeSideMenuOption, setActiveSideMenuOption] = useState<RangeWithCustom>('R_2W');

    const { data, isSuccess } = useQueryGetProjectDatesForResourceChart({
        lastMetaDataId: versionId,
        comparedMetaDataId: compareVersionId,
    });

    const formValue = methods?.getValues(name);

    useEffect(() => {
        if (!filtersStatusRef?.current || filtersStatusRef?.current[name]?.isReady || !isSuccess) {
            return;
        }
        if (!value || value.length === 0) {
            setActiveSideMenuOption('R_2W');
        }

        if (value && value.length > 0) {
            if (Array.isArray(value)) {
                methods?.setValue(name, value.map(Number));
                setActiveSideMenuOption('R_CUSTOM');
            } else {
                methods?.setValue(name, value);
                setActiveSideMenuOption(value);
            }
        }
        signalSingleFilterIsReady({ filterId: name, isReady: isSuccess });
    }, [value, methods, name, filtersStatusRef, isSuccess]);

    const inputLabel = useMemo(() => {
        if (typeof formValue === 'string') {
            return rangeOptions.find((item) => item.value === formValue)?.label || '';
        }
        if (Array.isArray(formValue)) {
            return getInputLabel({ startDate: Number(formValue[0]), endDate: Number(formValue[1]) });
        }
        return '';
    }, [formValue]);

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
        setMenuOpen(true);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
        setMenuOpen(false);
    };

    const onApply = (value: (number | null)[] | string) => {
        methods?.setValue(name, value);
        handleFilters && handleFilters();
        setMenuOpen(false);
    };

    return (
        <div className={`${classes.selectWrapper} ${componentClasses.inputWidth}`}>
            <TextInput
                id={name}
                placeholder={'Select date range...'}
                value={inputLabel}
                onClick={handleMenuOpen}
                autoComplete={'off'}
                onChange={noop}
                label={'Date Range'}
                clearable={false}
            />
            <Popover
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                disableScrollLock
                anchorEl={anchorEl}
                onClose={handlePopoverClose}
                open={menuOpen}
                sx={{
                    '.MuiPopover-paper': {
                        overflow: 'visible',
                    },
                    '.MuiPopover-root': {
                        overflow: 'visible',
                    },
                }}
            >
                <div data-testid={'popover-content'} className={componentClasses.popoverContentWrapper}>
                    <div className={componentClasses.popoverContent}>
                        <SideMenu<RangeWithCustom>
                            menuOptions={rangeOptions}
                            activeSideMenuOption={activeSideMenuOption}
                            setActiveSideMenuOption={setActiveSideMenuOption}
                            className={componentClasses.sideMenu}
                        />
                        <DateRangeFilterMain
                            activeSideMenuOption={activeSideMenuOption}
                            value={formValue}
                            onChange={onApply}
                            handlePopoverClose={handlePopoverClose}
                            minDate={data?.startProjectDate}
                            maxDate={data?.endProjectDate}
                        />
                    </div>
                </div>
            </Popover>
        </div>
    );
};
